export type CalculatorPlatform = 'ios' | 'android' | 'windows' | 'macos' | 'linux' | 'default';

/** Detects which native calculator look to mimic. Trusts Capacitor's own platform report first
 *  (this app already ships a native Android wrapper, with iOS in progress) — that's a genuine
 *  fact about the runtime, not a guess, unlike user-agent sniffing. Falls back to sniffing only
 *  for plain desktop/mobile browsers, where there's no native wrapper to ask.
 *
 *  'linux'/'default' both fall through to the calculator's own existing, already-shipped look —
 *  there's no single dominant "Linux calculator" appearance worth chasing (GNOME and KDE don't
 *  even agree with each other), so this is a deliberate no-op case, not a gap. */
export function detectCalculatorPlatform(): CalculatorPlatform {
  const capacitor = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string } }).Capacitor;
  if (capacitor?.isNativePlatform?.()) {
    const native = capacitor.getPlatform?.();
    if (native === 'ios') return 'ios';
    if (native === 'android') return 'android';
  }

  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';

  // iPadOS reports itself as "MacIntel" with no touch-point signal on a real Mac — the standard
  // trick to tell a browser-based iPad apart from an actual desktop Mac.
  const isIOS = /iPhone|iPad|iPod/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Win/.test(platform) || /Windows/.test(ua)) return 'windows';
  if (/Mac/.test(platform)) return 'macos';
  if (/Linux/.test(platform)) return 'linux';

  return 'default';
}
